import { Alert, AlertIcon, Text } from '@chakra-ui/react';

interface InlineErrorStateProps {
	/**
	 * Already-translated, plain-language message. No status codes — see
	 * `dashboard/DESIGN.md`, Empty & Error States → "4. Error".
	 */
	message: string;
}

/**
 * A compact, in-flow error notice for a single CONTROL whose data could not be
 * read — a picker, a select, a chip row inside a form.
 *
 * `ErrorState` is the page-level sibling (80px icon circle, 60px of vertical
 * padding, optional retry CTA); dropping that into a modal's form stack pushes
 * the actual form off screen. This one keeps the form's rhythm and still says
 * the one thing the operator needs: *we do not know what is there*.
 *
 * It exists because the alternative every call site reached for was silence.
 * When a list read is refused or fails, `data` is `undefined`, so
 * `(data ?? []).map(...)` renders an empty picker and `data?.length === 0`
 * renders "there is nothing here" — both of which are confident, actionable
 * and wrong. On 2026-09-12 an operator was sent to look at a venue recording
 * box that was live and heartbeating the whole time, because a 403 from an
 * admin-only list route painted as "No venue agents yet". See D57 in
 * `docs/automation/sentry-backlog-status.md`.
 */
export default function InlineErrorState({ message }: InlineErrorStateProps) {
	return (
		<Alert status="error" role="alert" borderRadius="8px" px={3} py={2}>
			<AlertIcon />
			<Text fontSize="sm">{message}</Text>
		</Alert>
	);
}
